<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  useAuth,
  useGameId,
  useHostBackground,
  useKeyboardSpin,
  useModal,
  usePostMessage,
  useThemeVars,
  useTokenRetry,
  useViewportVarsListener,
} from './composables'
import BalanceModal from './components/BalanceModal.vue'
import LoadingScreen, { type LoadingPhase } from './components/LoadingScreen.vue'
import VersionBadge from './components/VersionBadge.vue'
import SlowNetworkDialog from './components/SlowNetworkDialog.vue'
import { useVersionInfo } from './composables/useVersionInfo'
import { logGameEntry, resolveGameEntry } from './composables/useGameEntry'
import { useLoadTimeout } from './composables/useLoadTimeout'
import PageErrorScreen from './components/PageErrorScreen.vue'
import AuthErrorScreen from './components/AuthErrorScreen.vue'
import GameExitScreen from './components/GameExitScreen.vue'
// import OrientationOverlay from './components/OrientationOverlay.vue' // 與 template 同步暫時停用
// DevSpinPanel 只在 `pnpm dev`（本機）使用；用 import.meta.env.DEV（build 恆 false）
// 讓 `vite build` 把 DevSpinPanel.vue + config/devSeeds.ts 整包 tree-shake 出線上 bundle。
// ⚠️ 這是 Vite 內建的 dev/build 判別式，非 app-env（config.json.env），不違反 runtime env 規範。
const DevSpinPanel = import.meta.env.DEV
  ? defineAsyncComponent(() => import('./components/DevSpinPanel.vue'))
  : null
import ViewportDebugOverlay from './components/ViewportDebugOverlay.vue'
import { applyLocaleFromUrl } from './lang'
import { resolveNativeHelpComponent } from './games/registry'
import {
  loadGameImages,
  preloadGameImagesByTags,
  revokeAllGameImages,
} from './composables/useGameImages'
import { IMG_TAG_SCHEME } from './composables/useUtils'
import { useHostLoading } from './composables/useHostLoading'
import { preloadLoadingBg, resolveLoadingBg } from './composables/useLoadingAssets'
import { readGameId } from './composables/useGameId'
import { resolveLoadingTheme } from './config/loadingTheme'
import { LOADING_START_BUTTON } from './runtimeConfig'
import { setAuthRecoveryHandler } from './api/http'

const { t, locale } = useI18n()
const bootLocale = applyLocaleFromUrl()

/**
 * **開機第一件事就把 loading 背景圖預取下來**（2026-08-28）。
 *
 * 🛑 為什麼不等 `loadingBgUrl`（下方 computed）：`gameId` 是 `onMounted → initPage →
 *    parseFromLocation()` 才寫進 ref 的，而 Vue 的 **首次 render 早於 `onMounted`** ——
 *    等 ref 就會晚一步，`logo_animation.webp` 的 `<img>` 已經先進 DOM、先排進請求佇列。
 *    在 setup 期直接用 `readGameId()`（`useGameId.ts` 的**同一支**解析函式，不是另開解析點）
 *    從 URL 讀，preload link 就穩定排在 logo 之前。
 * 🛑 舊款零風險：沒放圖的 gameId → `resolveLoadingBg` 回 undefined → `preloadLoadingBg` 直接 return。
 */
preloadLoadingBg(
  resolveLoadingBg(
    typeof window === 'undefined' ? null : readGameId(new URL(window.location.href)),
    bootLocale,
  ),
)

// ========== Refs ==========
const gameFrame = ref<HTMLIFrameElement | null>(null)

// ========== Composables ==========
const {
  authToken,
  authUserInfo,
  authReady,
  authError,
  authCurrency,
  authBets,
  doAuthenticate,
  doRefreshToken,
  doLogout,
  handleAuthInvalid,
  resetAuth,
} = useAuth()

const {
  gameId,
  gameUrl,
  gameName,
  gameAssets,
  pageError,
  gameLoaded,
  parseFromLocation,
  fetchGameCode,
  markLoaded,
  reset: resetGameId,
} = useGameId()

// 是否已經收到過 GAME_READY（用於檢測重載）
const gameReadyReceived = ref(false)
// 游戲是否已退出
const gameExited = ref(false)

const postMessage = usePostMessage(
  {
    gameId,
    gameUrl,
    gameFrame,
    authToken,
    authUserInfo,
    authReady,
    currency: authCurrency,
  },
  {
    onOpenModal: (modalKey) => modal.openModal(modalKey),
    onAuthInvalid: () => void tokenRetry.refreshToken(),
    onGameReady: (payload) => {
      // 🛑 SET_CONFIG 必須是這裡的**第一件事**：Cocos `waitForConfig(3000)` 只等 3 秒，逾時會
      //    **靜默 fallback 到寫死的 dev 網域**（正式環境 100% 是錯的網域）。任何 await / 算
      //    loading 背景 / 取圖都排它後面。GAME_READY = listener 已掛好，現在送不會掉。
      postMessage.sendConfigToGame()
      // 新 loading 流程：讀 payload.hostLoading 決定遮罩收不收（🛑 要在 markLoaded 之前 —— 下方
      // 「彈窗圖預載」watch 靠 gameLoaded 觸發，觸發當下要已知道模式）。舊款 → 停錶、不介入。
      hostLoading.onGameReady(payload)
      markLoaded()
      gameReadyReceived.value = true
      postMessage.setChannelReady(true)
    },
    onLoadComplete: () => hostLoading.onLoadComplete(),
    onLoadError: (payload) => hostLoading.onLoadError(payload),
    onLoadAlive: () => hostLoading.onLoadAlive(),
    onExitGame: () => {
      gameExited.value = true
      doLogout()
    },
  },
)

// 新 loading 流程狀態機（docs/LOADING_PROTOCOL.md）：旗標判模式、LOAD_COMPLETE → LOADING_CLOSED、
// 心跳 re-arm、LOAD_ERROR 抑制窗。舊款（GAME_READY 沒旗標）全程不介入。
// 🛑 階段 2：沒有開始鈕，LOAD_COMPLETE 一到就自動送 LOADING_CLOSED（= kill-switch 退化行為）。
const hostLoading = useHostLoading({
  sendLoadingClosed: () => postMessage.sendLoadingClosedToGame(),
  // kill switch（runtime config.json.loadingStartButton=false）→ LOAD_COMPLETE 立刻自動關、不顯示按鈕
  autoClose: () => !LOADING_START_BUTTON,
  timeout: {
    arm: (stage) => loadTimeout.arm(stage),
    disarm: () => loadTimeout.disarm(),
    show: () => loadTimeout.show(),
  },
})

/** per-game loading 背景圖（bundled，`src/assets/loading/<gameId>/{zh,en}.*`；沒圖 → undefined） */
const loadingBgUrl = computed(() => resolveLoadingBg(gameId.value, locale.value))

// 開機那一發在 setup 期已經打掉（見上方 `preloadLoadingBg`）；這支是**之後才變**的情況：
// SPA 換款（popstate → initPage）或語系改變。`preloadLoadingBg` 對同一個 href 是 no-op，
// 所以開機這一輪 immediate 觸發不會重插也不會重抓。
watch(loadingBgUrl, (url) => preloadLoadingBg(url), { immediate: true })

/** per-game loading 配色（進度條 / 開始鈕）；沒設 → 空物件 → CSS 退回 `var(--color-theme)` */
const loadingThemeVars = computed(() => resolveLoadingTheme(gameId.value))

/**
 * 要不要顯示**新版 loading 版面**（背景圖 + 進度條 + 階段文字 + 輪播提示）。
 *
 * **開關只有一個來源：Cocos 的 `GAME_READY.payload.hostLoading`。**
 * 舊款不帶這個旗標 → 恆 false → 全程只有 logo，畫面跟改版前 100% 一樣。
 *
 * 🛑 **為什麼可以純看旗標、不會閃動**：前面壓了一段 logo splash
 *    （`LOGO_SPLASH_MS = 1500ms`），而 `GAME_READY` 實測約 **0.9s** 就到 ——
 *    等 `splashDone` 成立時旗標早就讀到了，直接進對的版面，中間不會先畫錯再修正。
 *    （`splashDone` 本來就必要：不然新款第一帧就成立，公司 logo 一次都不會渲染。）
 *    邊界：`GAME_READY` 慢於 1.5s 的新款，會看到一次 logo → 新版面的切換 ——
 *    那是「等太久」本來就該給的回饋，可接受。
 *
 * 🛑 **不要再拿「這款有沒有 loading 背景圖」當判斷條件**（2026-08-31 移除）。
 *    早期用 `hasLoadingBg()` 當 GAME_READY 之前的提示，副作用是「圖檔存在與否」
 *    隱式決定了行為 —— 圖一旦補齊（現在 85 款全有），所有舊款也會被翻成新版面。
 *    圖檔管理與行為必須解耦：**放圖只是準備素材，要不要用由 Cocos 決定。**
 */
const showNewLoadingUi = computed(
  () => hostLoading.splashDone.value && hostLoading.hostLoading.value,
)

/**
 * 進度條下方的**階段文字**（對齊競品 `#tips-text`，如「正在登录」）。
 * 🛑 這是 host **真實**知道的階段 —— 只有百分比是假的（協議 2.4）。三段對應：
 *   未拿到 token → `login`；已登入、等 iframe + GAME_READY → `game`；
 *   GAME_READY 後等 LOAD_COMPLETE（新款限定）→ `assets`。
 */
const loadingPhase = computed<LoadingPhase>(() => {
  // 進度補滿 100%、等玩家按開始 → 「完成 / Complete」
  if (hostLoading.loadComplete.value && hostLoading.progressDone.value) return 'complete'
  if (!authReady.value) return 'login'
  if (!gameLoaded.value) return 'game'
  return 'assets'
})
// 階段 → 假進度分段（login 0~20 / game 20~50 / assets 50~95，見 PROGRESS_SEGMENTS）。
// 🛑 綁在一起才能保證「正在加载资源」永遠從 50% 開始 —— 不綁的話前面一慢，
//    進度條會先衝到 88%，最長的那段只剩幾 % 可跑。
watch(
  loadingPhase,
  (p) => {
    if (p !== 'complete') hostLoading.setProgressPhase(p)
  },
  { immediate: true },
)

const modal = useModal(gameId, {
  enqueueToGame: postMessage.enqueueToGame,
})

// 401 → token 續期重試流程
const tokenRetry = useTokenRetry({
  doRefreshToken,
  authError,
  onSuccess: () => {
    postMessage.sendTokenToGame()
  },
  onTerminalFail: () => handleAuthInvalid(() => modal.closeAllModals()),
  t,
})
const { retryToastVisible, retryToastText } = tokenRetry

// host 自己打的 API 收到 401 時，讓 http 攔截器走同一條「重新 login / ticketLogin」流程
// （含 retry toast）。在 setup 期就註冊 —— initPage() 的 bootstrap 可能立刻用到。
// replay 根元件（ResultDetailApp）**不註冊**，所以那邊 401 維持直接顯示錯誤、不重試。
setAuthRecoveryHandler(() => tokenRetry.refreshToken())
onBeforeUnmount(() => setAuthRecoveryHandler(null))

// 當前 gameId 的 native help registry
const nativeHelpEntries = computed(() => {
  const id = gameId.value
  if (!id) return null
  const paytable = resolveNativeHelpComponent(id, 'paytable')
  const rules = resolveNativeHelpComponent(id, 'rules')
  const history = resolveNativeHelpComponent(id, 'history')
  if (!paytable && !rules && !history) return null
  return { paytable, rules, history }
})

const activeNativeHelpKey = computed(() => modal.activeNativeHelpModal.value?.key || null)
const activeNativeHelpId = computed(() => modal.activeNativeHelpModal.value?.id || null)

// ========== Computed ==========
/**
 * 原本要給 iframe 的 URL（= 轉接頁 `…/games/<code>/index.html`）。
 * 直解失敗時就是它，所以命名成 fallback；轉接頁**沒有被移除**，只是降級為保底。
 */
const gameFallbackSrc = computed(() => {
  if (!gameId.value || !gameUrl.value) return null
  return gameUrl.value
})

/**
 * 實際餵給 iframe 的 URL —— **外殼直解版本入口**（見 `composables/useGameEntry.ts`）。
 *
 * 外殼自己 fetch 一次 `<gameUrl 同目錄>/latest.json`，成功就把 iframe 直接指到
 * `<同目錄>/<sha>/index.html`，省掉轉接頁那一趟（下載 HTML → fetch → 整頁 replace）。
 * 失敗（404 / 逾時 / JSON 壞 / 非同源）一律回到 `gameFallbackSrc`，行為與改動前完全相同。
 *
 * 🛑 **不快取結果**：外殼是 SPA，切遊戲不重載頁面 —— 每次 gameUrl 變都重新解一次。
 */
const gameSrc = ref<string | null>(null)
let gameEntrySeq = 0
watch(
  gameFallbackSrc,
  (url) => {
    const my = ++gameEntrySeq
    // 換款 / 清空先把 iframe 拆掉，避免直解回來前還掛著上一款的 src
    gameSrc.value = null
    if (!url) return
    void resolveGameEntry(url).then((result) => {
      if (my !== gameEntrySeq) return // 期間又換過款 → 丟棄這個過期結果
      logGameEntry(result)
      gameSrc.value = result.src
    })
  },
  { immediate: true },
)

// 版本角標（server / cocos / web）：新版 per-game loading 左下角，隨遮罩收掉消失（見 template 註解）。
// 🛑 cocos 版本吃的是 **gameFallbackSrc**（原 gameUrl 同目錄的 gameConfig.json），不是直解後的
//    sha 目錄 —— 維持與直解改動前完全相同的取版行為。
const versionInfo = useVersionInfo(gameFallbackSrc, gameId, authReady)

// 載入停滯偵測（軟逾時）：卡太久跳「刷新 / 等待」對話框，**不中斷載入**。見 useLoadTimeout.ts。
const loadTimeout = useLoadTimeout()

/** 「刷新」：只 reload iframe，不 reload 整頁 —— ticket 已消耗，整頁重載會登入失敗。 */
function onSlowRefresh() {
  loadTimeout.slowVisible.value = false
  // 自我賦值是**故意的**：重設 src = 重載 iframe 的標準手法（同下方 onMessageFailed 的兜底）。
  // eslint-disable-next-line no-self-assign
  if (gameFrame.value) gameFrame.value.src = gameFrame.value.src
  loadTimeout.arm('iframeLoad')
}

const showLoading = computed(() => {
  if (pageError.value || authError.value) return false
  if (!gameId.value) return false
  // 新款（hostLoading）：gameLoaded 了遮罩還要蓋著，直到 LOADING_CLOSED 送出；舊款 maskActive 恆 false。
  return !authReady.value || !gameLoaded.value || hostLoading.maskActive.value
})

// 遮罩出現 → 假進度從 0 開始跑；遮罩收掉 → 停（換 gameId / reload 會再出現一次、再從 0 跑）。
// 補滿 / 歸零由 useHostLoading 內部管（LOAD_COMPLETE / reset）。
// 同時起算 logo splash（內部冪等、只跑一次 —— reload 不重播 logo）。
watch(
  showLoading,
  (on) => {
    if (on) {
      hostLoading.startSplash()
      hostLoading.startProgress()
    } else hostLoading.stopProgress()
  },
  { immediate: true },
)

const { pcBackgroundStyle, mobileBackgroundStyle } = useHostBackground(gameAssets)
useThemeVars(gameAssets)

const { cardW, cardH } = useViewportVarsListener()
const responsiveCardStyle = computed(() => {
  if (cardW.value <= 0) return { width: '', height: '' }
  return {
    width: cardW.value + 'px',
    height: cardH.value + 'px',
  }
})

const iframeStyle = computed(() => ({
  display: 'block',
  border: 'none',
  width: cardW.value > 0 ? cardW.value + 'px' : '100%',
  height: cardH.value > 0 ? cardH.value + 'px' : '100%',
}))

// ========== Event Handlers ==========
function onGameLoad() {
  if (gameReadyReceived.value) {
    postMessage.handleGameReload()
    // Cocos 重啟後狀態清空，host 的 modalStack 同步清除（不送 MODAL_CLOSED）
    modal.silentClearModals()
    // 新 loading 流程狀態歸零（Cocos 重啟會重送 GAME_READY，模式重新判）
    hostLoading.reset()
  }
  postMessage.sendTokenToGame()
  // iframe 本體載完 → 換錶等 GAME_READY（Cocos 引擎起來、listener 掛好）
  if (!gameReadyReceived.value) loadTimeout.arm('gameReady')
}

function onAuthInvalid() {
  handleAuthInvalid(() => modal.closeAllModals())
}

function close(modalId?: string) {
  modal.closeModal(modalId)
}

useKeyboardSpin({
  modalStack: modal.modalStack,
  gameExited,
  sendSpin: () => postMessage.sendSpinToGame(),
})

function returnToGame() {
  window.location.reload()
}

// ========== 頁面初始化 ==========
async function initPage() {
  const currentLocale = applyLocaleFromUrl()
  if (!parseFromLocation()) return

  resetAuth()
  resetGameId()
  gameReadyReceived.value = false
  hostLoading.reset()
  postMessage.setChannelReady(false)

  // gameId 由 parseFromLocation() 解出；bets 已改 per-game 端點 `/v1/{gameCode}/bets`，故要傳進去
  //
  // 🛑 這層 catch 不要拿掉：initPage 是 onMounted / popstate 裡 fire-and-forget 呼叫的，
  //    沒有 catch 的話任何未預期錯誤都會變成 unhandled rejection，畫面停在無限 loading
  //    （showLoading 要 authReady && gameLoaded 都 true 才會收起來）。寧可顯示錯誤畫面。
  try {
    await Promise.all([doAuthenticate(gameId.value), fetchGameCode(currentLocale)])
  } catch (e) {
    console.error('[web][App] initPage 失敗:', e)
    if (!pageError.value && !authError.value) {
      pageError.value = t('common.errors.networkError')
    }
  }
}

// ========== Lifecycle ==========
onMounted(() => {
  postMessage.initMessageQueue({
    onMessageFailed: (message, error) => {
      // MODAL_CLOSED 最終失敗 → Cocos 可能永久鎖住；LOADING_CLOSED 最終失敗 → 遊戲永遠不會開始。
      // 兩者都強制 reload iframe 解鎖（Cocos 端 LOADING_CLOSED 冪等，重送安全；ACK 全掉 = 通道真的壞）。
      if (message.action === 'MODAL_CLOSED' || message.action === 'LOADING_CLOSED') {
        console.warn(`[web][App] ${message.action} failed, reloading game iframe:`, error)
        modal.silentClearModals()
        postMessage.setChannelReady(false) // 先暫停佇列，避免 gap 期間送訊息給正在重啟的 iframe
        // 自我賦值是**故意的**：重設 src 是重載 iframe 的標準手法（只重載 Cocos，不動 host
        // 頁面；token 在記憶體、onGameLoad 會重發 SET_TOKEN）。見根 CLAUDE.md「iframe reload safety」。
        // eslint-disable-next-line no-self-assign
        if (gameFrame.value) gameFrame.value.src = gameFrame.value.src
      }
    },
  })
  initPage()
  window.addEventListener('popstate', initPage)
  postMessage.startListening()
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', initPage)
  postMessage.stopListening()
  hostLoading.stopProgress()
  revokeAllGameImages() // 回收 per-game 圖片 blob
})

// ========== Watchers ==========
// iframe 開始載 → 起錶（換 gameId / reload 都會重跑）；出錯頁時停錶（錯誤頁自己會顯示）。
watch(
  gameSrc,
  (src) => {
    if (src && !gameReadyReceived.value) loadTimeout.arm('iframeLoad')
    else if (!src) loadTimeout.disarm()
  },
  { immediate: true },
)
watch(
  () => [pageError.value, authError.value] as const,
  ([pe, ae]) => {
    if (pe || ae) loadTimeout.disarm()
  },
)

watch(
  () => [authReady.value, gameLoaded.value] as const,
  ([ready, loaded]) => {
    if (ready && loaded) postMessage.sendTokenToGame()
  },
)

// 例外快速通道：**主背景**（chrome.background）一登入就先抓 —— 它從進遊戲那刻就在畫面上
// （host 大底），跟整包一起延後會「一進遊戲沒背景」。只抓清單 + 1 張，頻寬可忽略；
// 整包載入會重用同一份清單、跳過已抓的 tag（見 useGameImages.preloadGameImagesByTags）。
watch(
  () => [authReady.value, gameId.value, gameAssets.value.background] as const,
  ([ready, id, bg]) => {
    if (ready && id && typeof bg === 'string' && bg.startsWith(IMG_TAG_SCHEME)) {
      void preloadGameImagesByTags(id, [bg.slice(IMG_TAG_SCHEME.length)])
    }
  },
  { immediate: true },
)

// 登入成功 + gameId 已知 + **遊戲本體已載完** → 才開始載該遊戲整包 modal 圖。
// 🛑 刻意等 gameLoaded：這批圖只有開賠付/規則/歷史彈窗才用得到，掛在 authReady
//    就載會在 boot 瞬間與 Cocos 遊戲本體的下載搶頻寬（zcm 66 張、sgcl 29 張）。
//    彈窗只能從遊戲內開（postMessage openModal）→ 能開彈窗時 gameLoaded 必為 true，
//    「圖還沒開始載就要用」不會發生；載入中開彈窗則由 resolveImg 的 reactive
//    blob pop-in 兜底。換 gameId 由 loadGameImages 內部 revoke 舊的。
// 新 loading 流程（hostLoading）再多等一關：**LOAD_COMPLETE 才載**（協議〈五〉已定案）——
// GAME_READY 當下 Cocos 正要載資源，那時開始抓圖等於跟遊戲本體搶頻寬。舊款 imagesAllowed 恆 true。
watch(
  () => [authReady.value, gameId.value, gameLoaded.value, hostLoading.imagesAllowed.value] as const,
  ([ready, id, loaded, allowed]) => {
    if (ready && id && loaded && allowed) void loadGameImages(id)
  },
  { immediate: true },
)

watch(
  gameName,
  (name) => {
    if (typeof document === 'undefined') return
    if (name) document.title = name
  },
  { immediate: true },
)
</script>

<template>
  <OrientationOverlay />
  <DevSpinPanel v-if="DevSpinPanel" :game-id="gameId" @open-modal="modal.openModal($event)" />
  <ViewportDebugOverlay />

  <main class="relative h-full w-full overflow-hidden">
    <div class="host-bg host-bg-pc" :style="pcBackgroundStyle" />
    <div class="host-bg host-bg-mobile" :style="mobileBackgroundStyle" />

    <!-- 頁面錯誤（gameId 相關） -->
    <PageErrorScreen v-if="pageError" :message="pageError" />

    <!-- 認證錯誤提示頁（v-else-if：pageError 優先，兩者可能同時發生 —— initPage 的
         doAuthenticate 與 fetchGameCode 是並行的，兩邊都可能失敗）-->
    <AuthErrorScreen v-else-if="authError" :message="authError" />

    <LoadingScreen
      :loading="showLoading"
      :host-loading="showNewLoadingUi"
      :progress="hostLoading.progress.value"
      :phase="loadingPhase"
      :show-start="hostLoading.showStartButton.value"
      :bg-url="loadingBgUrl"
      :theme-vars="loadingThemeVars"
      @start="hostLoading.closeLoading()"
    >
      <!-- 版本角標：貼 loading 圖左下角（掛在 .loading-frame 內，跟遮罩一起收）。
           🛑 不綁 `!gameReadyReceived`：新版 UI（hostLoading 分支）要 GAME_READY **之後**
           才會切過去，綁了會跟它互斥 → 可見窗口為零。badge 生命週期 = 遮罩存活期間。
           🛑 也不能放 responsive-card 內 —— game-wrapper 是 z-10 stacking context，被遮罩蓋死。 -->
      <VersionBadge
        :server="versionInfo.server.value"
        :cocos="versionInfo.cocos.value"
        :web="versionInfo.web.value"
      />
    </LoadingScreen>

    <!-- 載入停滯的軟逾時提示（刷新 / 等待）—— 蓋在 LoadingScreen 之上，不中斷載入 -->
    <SlowNetworkDialog
      :show="loadTimeout.slowVisible.value"
      @refresh="onSlowRefresh"
      @wait="loadTimeout.dismiss()"
    />

    <!-- 游戲退出頁面 -->
    <GameExitScreen v-if="gameExited" @return="returnToGame" />

    <template v-if="!pageError && !authError && !gameExited">
      <div class="game-wrapper">
        <div class="responsive-card" :style="responsiveCardStyle">
          <!--
            `allow="autoplay"`：把 autoplay 權限**委派**給遊戲 iframe（BGM 由 Cocos 自己播，
            host 不介入；**開始鈕刻意不接音效**，競品那顆也沒有）。
            同網域時本來就有這個權限（Permissions Policy 預設 allowlist = `self`），
            **跨網域才需要**（本機 dev 指到 dev-h5、或未來遊戲換網域）→ 留著當保險。
            🛑 別把它當「host 點一下就能解鎖 iframe 音訊」—— 自動播放政策是 per-document，
            按在 host document 的手勢不會讓 iframe 的 AudioContext 變 running。
            見 docs/LOADING_HOST_HANDOFF.md〈四〉。
          -->
          <iframe
            ref="gameFrame"
            :style="iframeStyle"
            :key="gameSrc || 'no-game'"
            :src="gameSrc || undefined"
            title="Cocos Game"
            frameborder="0"
            allowfullscreen
            allow="autoplay"
            @load="onGameLoad"
          />
          <!-- 余額彈窗（van-popup 自帶上滑動畫，不再包 App 層 Transition）-->
          <BalanceModal
            v-if="modal.activeBalanceModal.value && gameId"
            :game-id="gameId"
            :modal-id="modal.activeBalanceModal.value.id"
            :token="authToken || undefined"
            :wallet-icon="gameAssets.walletIcon"
            :tint-wallet-icon="gameAssets.tintWalletIcon"
            :wallet-label="gameAssets.walletLabel"
            :wallet-label-en="gameAssets.walletLabelEn"
            :currency="authCurrency || undefined"
            :currency-name="authBets?.name || undefined"
            @close="close"
            @auth-invalid="onAuthInvalid"
          />

          <!-- Native help 彈窗（paytable / rules / history）-->
          <template v-if="nativeHelpEntries && gameId">
            <component
              v-if="nativeHelpEntries.paytable"
              :is="nativeHelpEntries.paytable"
              :show="activeNativeHelpKey === 'paytable'"
              :modal-id="activeNativeHelpKey === 'paytable' ? activeNativeHelpId : null"
              :game-id="gameId"
              @close="close"
            />
            <component
              v-if="nativeHelpEntries.rules"
              :is="nativeHelpEntries.rules"
              :show="activeNativeHelpKey === 'rules'"
              :modal-id="activeNativeHelpKey === 'rules' ? activeNativeHelpId : null"
              :game-id="gameId"
              @close="close"
            />
            <component
              v-if="nativeHelpEntries.history"
              :is="nativeHelpEntries.history"
              :show="activeNativeHelpKey === 'history'"
              :modal-id="activeNativeHelpKey === 'history' ? activeNativeHelpId : null"
              :game-id="gameId"
              @close="close"
            />
          </template>

          <!-- 網絡重試 Toast -->
          <Transition name="retry-toast-fade">
            <div v-if="retryToastVisible" class="retry-toast">
              {{ retryToastText }}
            </div>
          </Transition>
        </div>
      </div>
    </template>
  </main>
</template>
